import { useGearsServiceUrl } from "@made-people/gears-configuration";
import {
  AlgoliaAutoCompleteProvider,
  AlgoliaProvider,
  AlgoliaProviderProps,
} from "@made-people/grease-algolia";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getAlgoliaClient } from "./get-algolia-client";
import {
  itemIdDimensionParser,
  mmAlgoliaCentraShopProductMapper,
} from "./mm-algolia-centra-shop-product-mapper";
import { productImageKey } from "../centra/mappers/utils/get-centra-mapper-options";
import { mmAlgoliaFacetToGreaseFacetValue } from "./mm-algolia-facet-to-grease-facet-value";
import { getOrCreateAlgoliaUserToken } from "./get-or-create-algolia-user-token";
import { resolveColorFacetValue } from "./mm-color-facet-mapper";

export type AlgoliaBootstrapProps = PropsWithChildren<{
  language: string;
  market: string;
  pricelist: string;
  countryCode: string;
}>;

export const AlgoliaBootstrap = (props: AlgoliaBootstrapProps) => {
  const algoliaBaseUrl = useGearsServiceUrl("algolia");
  const queryClient = useQueryClient();

  const { language, market } = props;

  const [algoliaUserToken, setAlgoliaUserToken] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    setAlgoliaUserToken(getOrCreateAlgoliaUserToken());
  }, []);

  const algoliaClientDataGetter = useMemo(
    () => () =>
      queryClient.fetchQuery({
        queryKey: ["algolia-client", language, market, algoliaBaseUrl],
        queryFn: () => getAlgoliaClient(language, market, algoliaBaseUrl),
        staleTime: Infinity,
      }),
    [queryClient, language, market, algoliaBaseUrl],
  );

  const algoliaProviderProps: AlgoliaProviderProps = useMemo(
    () => ({
      algoliaConfig: {
        mappers: {
          centraShopProduct: (product) =>
            mmAlgoliaCentraShopProductMapper(
              product,
              {
                language: language,
                market: props.market,
                pricelist: props.pricelist,
              },
              {
                productImageKey,
                itemIdDimensionParser,
              },
              props.countryCode,
            ),
          algoliaFacetToGreaseFacetValue: mmAlgoliaFacetToGreaseFacetValue,
          resolveFacetValueForFilter: (facetId, valueId) => {
            if (facetId === "color") {
              return resolveColorFacetValue(valueId);
            }
            return valueId;
          },
        },
        insights: algoliaUserToken
          ? { userToken: algoliaUserToken }
          : undefined,
      },
      algoliaClientDataGetter,
    }),
    [
      language,
      market,
      algoliaBaseUrl,
      props.pricelist,
      props.countryCode,
      algoliaUserToken,
    ],
  );

  return (
    <AlgoliaProvider {...algoliaProviderProps}>
      <AlgoliaAutoCompleteProvider>
        {props.children}
      </AlgoliaAutoCompleteProvider>
    </AlgoliaProvider>
  );
};
